import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { StageChip } from "@/components/StageChip";
import { loadMeta } from "@/lib/meta";
import { FIELDS as FF } from "@/lib/fields";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import { Eye, ArrowLeft, Loader2 } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function PreviewPartner() {
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState("");
  const [spoc, setSpoc] = useState("");
  const [spocs, setSpocs] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);

  useEffect(() => { loadMeta().then((m) => { setCompanies((m.selects || {})[FF.PARTNER_COMPANY] || []); setSpocs((m.selects || {})[FF.PARTNER_SPOC] || []); }); }, []);

  const runLoad = async (co, sp) => {
    setSel(null); setLoading(true);
    try { setCases((await api.get("/admin/partner-preview/cases", { params: { company: co, spoc: sp || undefined } })).data.cases); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  const onCompany = (co) => { setCompany(co); runLoad(co, spoc); };
  const onSpoc = (sp) => { const v = sp === "all" ? "" : sp; setSpoc(v); if (company) runLoad(company, v); };
  const open = async (rid) => {
    setLoading(true);
    try { setSel((await api.get(`/admin/partner-preview/cases/${rid}`, { params: { company, spoc: spoc || undefined } })).data); }
    catch (e) { toast.error("Failed"); }
    finally { setLoading(false); }
  };

  const f = (id) => sel?.fields?.[id];

  return (
    <Layout>
      <div className="flex items-center gap-2 mb-5">
        <Eye className="w-5 h-5 text-[#FF4802]" />
        <h1 className="text-2xl font-semibold tracking-tight text-[#333333]">Preview as partner</h1>
        <span className="text-xs bg-[#F5B71D]/20 text-[#92600a] px-2 py-0.5 rounded-full font-medium">Read-only · live cases</span>
      </div>

      <div className="rounded-2xl border border-[#E2E2E2] bg-white p-4 mb-4 shadow-sm flex items-center gap-3 flex-wrap">
        <span className="text-sm text-[#6B7280]">Partner company</span>
        <Select value={company || ""} onValueChange={onCompany}>
          <SelectTrigger data-testid="preview-company" className="w-56 border-[#E2E2E2] rounded-lg"><SelectValue placeholder="Choose a partner (e.g. Loan Network)" /></SelectTrigger>
          <SelectContent className="bg-white rounded-xl border-[#E2E2E2] max-h-60">
            {companies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-[#6B7280]">SPOC</span>
        <Select value={spoc || "all"} onValueChange={onSpoc}>
          <SelectTrigger data-testid="preview-spoc" className="w-52 border-[#E2E2E2] rounded-lg"><SelectValue placeholder="All SPOCs" /></SelectTrigger>
          <SelectContent className="bg-white rounded-xl border-[#E2E2E2] max-h-60">
            <SelectItem value="all">All SPOCs (company view)</SelectItem>
            {spocs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading && <div className="flex items-center gap-2 text-[#6B7280] text-sm py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#FF4802]" /> Loading…</div>}

      {!loading && sel && (
        <div>
          <button onClick={() => setSel(null)} className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#333333] mb-3"><ArrowLeft className="w-4 h-4" /> Back to list</button>
          <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm mb-4 flex items-center gap-4">
            <span className="font-mono text-sm font-semibold text-[#E2521B] bg-[#FF4802]/5 px-2.5 py-1 rounded-lg border border-[#FF4802]/20">{sel.case_id}</span>
            <h2 className="text-lg font-semibold text-[#333333]">{f(FF.BORROWER) || f(FF.NAME)}</h2>
            <StageChip stage={f(FF.LEAD_STAGE)} />
          </div>
          <div className="rounded-2xl border border-[#E2E2E2] bg-white p-6 shadow-sm grid grid-cols-2 gap-x-6 gap-y-3" data-testid="preview-case-detail">
            {[["Name", f(FF.NAME)], ["Borrower", f(FF.BORROWER)], ["Co-Borrower", f(FF.CO_BORROWER)],
              ["Loan amount", formatINR(f(FF.LOAN_AMOUNT))], ["Loan Tenure", f(FF.LOAN_TENURE)],
              ["Bank", Array.isArray(f(FF.BANK_NAME)) ? f(FF.BANK_NAME).join(", ") : f(FF.BANK_NAME)],
              ["Property", f(FF.PROPERTY_NAME_OOT)],
              ["HL RM", Array.isArray(f(FF.HL_RM)) ? f(FF.HL_RM).join(", ") : f(FF.HL_RM)]].map(([k, v]) => (
              <div key={k}><div className="text-[11px] uppercase text-[#6B7280]" style={{ letterSpacing: "0.06em" }}>{k}</div><div className="text-sm text-[#333333] mt-0.5">{v || "—"}</div></div>
            ))}
          </div>
          <p className="text-xs text-[#6B7280] mt-3">This is exactly the whitelisted data a partner sees. Notes, commission, documents and passwords are never exposed.</p>
        </div>
      )}

      {/* Property field hidden of Micro Market per Build #3 P4 */}
      {!loading && !sel && company && (
        <div className="w-full overflow-hidden rounded-2xl border border-[#E2E2E2] bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead><tr>{["Case", "Borrower", "Stage", "Loan", "Bank(s)"].map((h) => <th key={h} className="bg-[#F7F7F7] text-[#6B7280] font-medium text-xs py-3 px-3.5 text-left border-b border-[#E2E2E2] uppercase tracking-wider">{h}</th>)}</tr></thead>
            <tbody>
              {cases.length === 0 && <tr><td colSpan={5} className="text-center text-sm text-[#6B7280] py-10">No cases for this partner.</td></tr>}
              {cases.map((c) => (
                <tr key={c.record_id} onClick={() => open(c.record_id)} data-testid={`preview-row-${c.case_id}`} className="border-b border-[#F7F7F7] hover:bg-[#F6F5EF]/70 cursor-pointer text-xs">
                  <td className="py-2.5 px-3.5 whitespace-nowrap"><span className="font-mono font-semibold text-[#E2521B]">{c.case_id}</span></td>
                  <td className="py-2.5 px-3.5 text-[#333333]">{c.borrower || "—"}</td>
                  <td className="py-2.5 px-3.5"><StageChip stage={c.stage} /></td>
                  <td className="py-2.5 px-3.5 font-mono whitespace-nowrap">{formatINR(c.loan_amount)}</td>
                  <td className="py-2.5 px-3.5 text-[#6B7280]">{Array.isArray(c.banks) ? c.banks.join(", ") : c.banks || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
